import { useEffect, useMemo, useState } from "react";
import { teamPlayerSlots } from "../../../registry/utils/courtTeams";
import { resolvePlayerPhotoUrls } from "../../../../repositories/playerPhotoUrlCache";
import { IconPersonSmall } from "./MatchControlIcons";
import {
  courtPlayerPositions,
  serveNetOffsetClass,
  singlesCourtPosition,
  slotHasServeIndicator,
  doublesPlayersForCourtDisplay,
} from "../utils/courtGeometry";

/**
 * @param {object} props
 * @param {"serviceBoxes"|"fixedEnds"} [props.playerStance]
 *   serviceBoxes — badminton/tennis/PB (players move L/R with serve court)
 *   fixedEnds — table tennis (players stay on their end; no even/odd court parity)
 * @param {number|null} [props.serverIndex] — doubles server slot within serving side
 */
export default function CourtPlayerMarkers({
  portrait,
  endsSwapped,
  teamA,
  teamB,
  vbW,
  vbH,
  serving,
  scoreA,
  scoreB,
  serveRound,
  showServeMarker,
  /** Engine serve court: "right"|"left" (tennis/PB); TT doubles always "right"; TT singles null */
  serveCourt = null,
  playerStance = "serviceBoxes",
  serverIndex = 0,
}) {
  const topSide = endsSwapped ? "B" : "A";
  const bottomSide = endsSwapped ? "A" : "B";
  const leftSide = endsSwapped ? "B" : "A";
  const rightSide = endsSwapped ? "A" : "B";
  const teamFor = (side) => (side === "A" ? teamA : teamB);
  const colorFor = (side) =>
    side === "A" ? "var(--tra-color-team-a)" : "var(--tra-color-team-b)";

  const playerIdsNeedingPhoto = useMemo(() => {
    const ids = [];
    for (const team of [teamA, teamB]) {
      for (const slot of teamPlayerSlots(team)) {
        if (slot.playerId && !slot.avatar) ids.push(slot.playerId);
      }
    }
    return [...new Set(ids)];
  }, [teamA, teamB]);

  const [resolvedPhotos, setResolvedPhotos] = useState(() => ({}));

  useEffect(() => {
    if (!playerIdsNeedingPhoto.length) return undefined;
    let cancelled = false;
    void resolvePlayerPhotoUrls(playerIdsNeedingPhoto).then((map) => {
      if (cancelled) return;
      setResolvedPhotos((prev) => ({ ...prev, ...map }));
    });
    return () => {
      cancelled = true;
    };
  }, [playerIdsNeedingPhoto.join("|")]);

  const photoFor = (slot) => {
    if (slot?.avatar) return slot.avatar;
    if (slot?.playerId && resolvedPhotos[slot.playerId]) {
      return resolvedPhotos[slot.playerId];
    }
    return null;
  };

  const halves = portrait
    ? [
        { half: "top", side: topSide },
        { half: "bottom", side: bottomSide },
      ]
    : [
        { half: "left", side: leftSide },
        { half: "right", side: rightSide },
      ];

  const playerNodes = halves.flatMap(({ half, side }) => {
    const team = teamFor(side);
    const rawSlots = teamPlayerSlots(team);
    if (!rawSlots.length) return [];
    const isDoubles = rawSlots.length > 1 || team?.type === "doubles";
    const ttDoubles = playerStance === "fixedEnds" && isDoubles;
    const players = isDoubles
      ? playerStance === "fixedEnds"
        ? rawSlots
        : doublesPlayersForCourtDisplay(portrait, rawSlots, {
            side,
            serving,
            scoreA,
            scoreB,
            endsSwapped,
          })
      : rawSlots;
    const slots = isDoubles
      ? ttDoubles
        ? [
            singlesCourtPosition(
              portrait,
              side,
              serving,
              scoreA,
              scoreB,
              endsSwapped,
              "right",
              "serviceBoxes"
            ),
            singlesCourtPosition(
              portrait,
              side,
              serving,
              scoreA,
              scoreB,
              endsSwapped,
              "left",
              "serviceBoxes"
            ),
          ]
        : courtPlayerPositions(portrait, half, true)
      : [
          singlesCourtPosition(
            portrait,
            side,
            serving,
            scoreA,
            scoreB,
            endsSwapped,
            null,
            playerStance
          ),
        ];
    return players.slice(0, slots.length).map((slot, i) => {
      const name =
        typeof slot === "string" ? slot : slot?.displayName || "";
      const photo = typeof slot === "string" ? null : photoFor(slot);
      const pos = slots[i] || slots[0];
      const showServe =
        showServeMarker &&
        slotHasServeIndicator(
          portrait,
          side,
          serving,
          scoreA,
          scoreB,
          endsSwapped,
          pos,
          ttDoubles ? "right" : serveCourt,
          playerStance,
          {
            serverIndex: serverIndex || 0,
            slotIndex: i,
            doublesDiagonal: ttDoubles,
          }
        );
      return (
        <div
          key={`${side}-${half}-${i}`}
          className={`court-player-marker${portrait ? "" : " landscape-name-top"}`}
          style={{
            left: `${(pos.x / vbW) * 100}%`,
            top: `${(pos.y / vbH) * 100}%`,
            color: colorFor(side),
          }}
        >
          <div className="court-player-icon">
            {photo ? (
              <img
                className="court-player-photo"
                src={photo}
                alt=""
                decoding="async"
              />
            ) : (
              <IconPersonSmall stroke={colorFor(side)} size={16} />
            )}
          </div>
          <span className="court-player-name">{name}</span>
          {showServe && (
            <div
              className={`serve-marker-adjacent ${serveNetOffsetClass(portrait, half)}${
                serveRound ? "" : " serve-marker-sq"
              }`}
              aria-hidden
            />
          )}
        </div>
      );
    });
  });

  return <>{playerNodes}</>;
}
